import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../../components/feature/Navbar";
import Footer from "../../../components/feature/Footer";
import { supabase } from "../../../lib/supabase";
import { useApp } from "../../../contexts/AppContext";
import { useCategories } from "../../../hooks/useCategories";

function getKitRecommendedPrice(purchasePrice: number, plan: "free" | "light"): { light: number; free: number; rate: string } {
  let lightRate = 1.50, freeRate = 1.60, rateLabel = "〜3,000円";
  if (purchasePrice > 15000) { lightRate = 1.35; freeRate = 1.45; rateLabel = "15,001円〜"; }
  else if (purchasePrice > 8000) { lightRate = 1.40; freeRate = 1.50; rateLabel = "8,001〜15,000円"; }
  else if (purchasePrice > 3000) { lightRate = 1.45; freeRate = 1.55; rateLabel = "3,001〜8,000円"; }
  return {
    light: Math.round(purchasePrice * lightRate),
    free: Math.round(purchasePrice * freeRate),
    rate: rateLabel,
  };
}

export default function SellKitPage() {
  const { currentUser } = useApp();
  const [step, setStep] = useState(1);
  const currentPlan: "free" | "light" = (currentUser?.plan ?? "free") as "free" | "light";
  const [form, setForm] = useState({ name: "", category: "", stock: "", purchasePrice: "", price: "", description: "", kitContents: "", shippingNote: "", visibility: "public" });
  const [completed, setCompleted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const { categories, refetch } = useCategories();
  const [showNewCat, setShowNewCat] = useState(false);
  const [newCatName, setNewCatName] = useState("");
  const [addingCat, setAddingCat] = useState(false);

  const handleAddCategory = async () => {
    if (!newCatName.trim() || !currentUser?.id) return;
    setAddingCat(true);
    const maxOrder = categories.length > 0 ? Math.max(...categories.map((c) => c.sort_order ?? 0)) : 0;
    const { error: err } = await supabase.from("categories").insert({
      name: newCatName.trim(),
      icon: "ri-box-3-line",
      visible: true,
      sort_order: maxOrder + 1,
      status: "approved",
      created_by: currentUser.id,
    });
    setAddingCat(false);
    if (!err) {
      setForm(f => ({ ...f, category: newCatName.trim() }));
      setNewCatName("");
      setShowNewCat(false);
      if (refetch) refetch();
    }
  };

  const purchasePrice = parseInt(form.purchasePrice) || 0;
  const recommended = purchasePrice > 0 ? getKitRecommendedPrice(purchasePrice, currentPlan) : null;
  const myPrice = parseInt(form.price) || 0;

  const handleComplete = async () => {
    if (step < 4) { setStep(s => s + 1); return; }
    if (!currentUser?.id) { setError("ログインが必要です"); return; }
    setSubmitting(true);
    const { error: err } = await supabase.from("products").insert({
      provider_id: currentUser.id,
      title: form.name || "無題の教材",
      type: "kit",
      price: parseInt(form.price) || 0,
      description: form.description,
      category: form.category,
      status: "public",
      stock_count: parseInt(form.stock) || 0,
      purchase_price: parseInt(form.purchasePrice) || 0,
      kit_contents: form.kitContents,
      shipping_note: form.shippingNote,
      visibility: form.visibility,
      sales_count: 0,
    });
    setSubmitting(false);
    if (err) { setError(err.message); return; }
    setCompleted(true);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-ivory flex items-center justify-center">
        <div className="text-center">
          <div className="w-24 h-24 flex items-center justify-center bg-gold rounded-full mx-auto mb-6 animate-bounce">
            <i className="ri-checkbox-circle-fill text-white text-5xl" />
          </div>
          <h2 className="font-serif text-deep-green text-3xl mb-3">出品完了！</h2>
          <p className="text-charcoal/60 font-sans mb-6">教材キットが公開されました。</p>
          <div className="flex gap-3 justify-center">
            <Link to="/mypage/provider" className="bg-deep-green text-white px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">マイページへ</Link>
            <Link to="/sell" className="border border-deep-green text-deep-green px-6 py-3 rounded-full text-sm font-sans cursor-pointer hover:bg-ivory transition-colors whitespace-nowrap">出品トップへ</Link>
          </div>
        </div>
      </div>
    );
  }

  const steps = ["基本情報", "画像・在庫", "価格計算", "詳細・公開"];

  return (
    <div className="min-h-screen bg-ivory">
      <Navbar />
      <main className="pt-20">
        <section className="bg-deep-green py-10 px-4">
          <div className="max-w-screen-xl mx-auto">
            <div className="flex items-center gap-3 mb-4">
              <Link to="/sell" className="text-white/50 text-sm font-sans cursor-pointer hover:text-white">出品トップ</Link>
              <i className="ri-arrow-right-s-line text-white/30" />
              <p className="text-white text-sm font-sans">教材キットを出品</p>
            </div>
            <h1 className="font-serif text-white text-3xl font-light">教材キットを出品</h1>
          </div>
        </section>

        {/* Step Indicator */}
        <section className="bg-white border-b border-stone-100 px-4 py-4 sticky top-16 z-20">
          <div className="max-w-2xl mx-auto flex items-center gap-2">
            {steps.map((s, i) => (
              <div key={s} className="flex items-center flex-1">
                <button onClick={() => i + 1 < step && setStep(i + 1)} className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-sans flex-shrink-0 transition-all cursor-pointer ${i + 1 < step ? "bg-gold text-white" : i + 1 === step ? "bg-deep-green text-white" : "bg-stone-200 text-charcoal/40"}`}>
                  {i + 1 < step ? <i className="ri-check-line" /> : i + 1}
                </button>
                <p className={`text-xs font-sans ml-1.5 whitespace-nowrap hidden sm:block ${i + 1 === step ? "text-deep-green font-medium" : "text-charcoal/40"}`}>{s}</p>
                {i < steps.length - 1 && <div className={`flex-1 h-px mx-2 ${i + 1 < step ? "bg-gold" : "bg-stone-200"}`} />}
              </div>
            ))}
          </div>
        </section>

        <section className="px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {step === 1 && (
              <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-5">
                <h2 className="font-serif text-deep-green text-lg">基本情報</h2>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">商品名 <span className="text-red-400">*</span></label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors" placeholder="例：プロの道具キット 完全セット" />
                </div>
                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">カテゴリ <span className="text-red-400">*</span></label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors bg-white cursor-pointer">
                    <option value="">選択してください</option>
                    {categories.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                  {!showNewCat ? (
                    <button
                      type="button"
                      onClick={() => setShowNewCat(true)}
                      className="mt-1.5 text-xs text-teal font-sans flex items-center gap-1 cursor-pointer hover:text-deep-green transition-colors"
                    >
                      <i className="ri-add-circle-line" />
                      カテゴリを新規追加する
                    </button>
                  ) : (
                    <div className="mt-2 flex items-center gap-2 bg-teal/5 border border-teal/20 rounded-xl p-3">
                      <input
                        value={newCatName}
                        onChange={e => setNewCatName(e.target.value)}
                        placeholder="新しいカテゴリ名を入力"
                        maxLength={40}
                        className="flex-1 bg-transparent border-none outline-none text-sm font-sans text-charcoal placeholder-charcoal/30"
                        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); handleAddCategory(); } }}
                      />
                      <button
                        type="button"
                        onClick={handleAddCategory}
                        disabled={addingCat || !newCatName.trim()}
                        className="px-3 py-1.5 bg-teal text-white text-xs rounded-lg font-sans cursor-pointer disabled:opacity-60 whitespace-nowrap hover:bg-deep-green transition-colors"
                      >
                        {addingCat ? <i className="ri-loader-4-line animate-spin" /> : "追加"}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setShowNewCat(false); setNewCatName(""); }}
                        className="w-6 h-6 flex items-center justify-center text-charcoal/30 hover:text-charcoal cursor-pointer"
                      >
                        <i className="ri-close-line text-sm" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h2 className="font-serif text-deep-green text-lg mb-4">商品画像</h2>
                  <div className="grid grid-cols-3 gap-3">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="aspect-square border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center hover:border-gold transition-colors cursor-pointer">
                        <i className={`${i === 0 ? "ri-image-add-line" : "ri-add-line"} text-2xl text-charcoal/20`} />
                        <p className="text-xs text-charcoal/30 font-sans mt-1">{i === 0 ? "メイン画像" : `画像 ${i + 1}`}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-sans text-sm font-medium text-charcoal mb-4">在庫数</h3>
                  <input type="number" min="0" value={form.stock} onChange={e => setForm(f => ({ ...f, stock: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors" placeholder="例：50（0で売り切れ表示）" />
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-serif text-deep-green text-lg">価格設定</h2>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-sans ${currentPlan === "light" ? "bg-gold/10 text-gold" : "bg-stone-100 text-charcoal/50"}`}>{currentPlan === "light" ? "ライトプラン" : "フリープラン"}</span>
                </div>

                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">仕入れ価格（Amazon等の購入単価）</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40 font-sans text-sm">¥</span>
                    <input type="number" min="0" value={form.purchasePrice} onChange={e => setForm(f => ({ ...f, purchasePrice: e.target.value }))} className="w-full border border-stone-200 rounded-xl pl-8 pr-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors" placeholder="0" />
                  </div>
                  <p className="text-xs text-charcoal/40 font-sans mt-1">仕入れ価格を入力すると、プランに応じた推奨販売価格が表示されます</p>
                </div>

                {recommended && (
                  <div className="bg-stone-50 rounded-xl p-4 space-y-3">
                    <p className="text-xs text-charcoal/50 font-sans">仕入れ価格 ¥{purchasePrice.toLocaleString()}（{recommended.rate}の区分）</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`p-3 rounded-xl border-2 ${currentPlan === "light" ? "border-gold bg-gold/5" : "border-stone-200 bg-white"}`}>
                        <p className="text-xs text-charcoal/40 font-sans mb-1">ライトプラン推奨価格</p>
                        <p className="font-serif text-xl text-gold">¥{recommended.light.toLocaleString()}</p>
                        {currentPlan === "light" && <p className="text-xs text-gold font-sans mt-1">← あなたのプラン</p>}
                      </div>
                      <div className={`p-3 rounded-xl border-2 ${currentPlan === "free" ? "border-teal bg-teal/5" : "border-stone-200 bg-white"}`}>
                        <p className="text-xs text-charcoal/40 font-sans mb-1">フリープラン推奨価格</p>
                        <p className="font-serif text-xl text-teal">¥{recommended.free.toLocaleString()}</p>
                        {currentPlan === "free" && <p className="text-xs text-teal font-sans mt-1">← あなたのプラン</p>}
                      </div>
                    </div>
                    <p className="text-xs text-charcoal/40 font-sans">手数料・送料・梱包コストを考慮した上で最終価格を設定してください。</p>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-sans text-charcoal/60 mb-1.5">販売価格（円）<span className="text-red-400">*</span></label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40 font-sans text-sm">¥</span>
                    <input type="number" min="0" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} className="w-full border border-stone-200 rounded-xl pl-8 pr-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors" placeholder={recommended ? `推奨：¥${currentPlan === "light" ? recommended.light.toLocaleString() : recommended.free.toLocaleString()}` : "例：7,250"} />
                  </div>
                </div>
                {recommended && myPrice > 0 && (
                  <div className={`rounded-xl p-3 text-xs font-sans ${myPrice < recommended[currentPlan] * 0.8 ? "bg-amber-50 border border-amber-200 text-amber-700" : "bg-teal/5 border border-teal/20 text-teal"}`}>
                    {myPrice < recommended[currentPlan] * 0.8 ? <><i className="ri-information-line mr-1" />設定価格が推奨価格より20%以上低くなっています。手数料・送料を含めた利益をご確認ください。</> : <><i className="ri-checkbox-circle-line mr-1" />適切な価格帯です。</>}
                  </div>
                )}
              </div>
            )}

            {step === 4 && (
              <div className="space-y-5">
                <div className="bg-white rounded-2xl p-6 border border-stone-100 space-y-4">
                  <h2 className="font-serif text-deep-green text-lg">詳細情報</h2>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">商品説明</label>
                    <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={4} maxLength={500} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors resize-none" placeholder="このキットでできること、対応講座との連携..." />
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">キット内容（何が入っているか）</label>
                    <textarea value={form.kitContents} onChange={e => setForm(f => ({ ...f, kitContents: e.target.value }))} rows={4} maxLength={500} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors resize-none" placeholder="例：&#10;・専用しみ抜き液 50ml × 2本&#10;・柔毛ブラシ × 1本&#10;・説明書 × 1枚" />
                  </div>
                  <div>
                    <label className="block text-xs font-sans text-charcoal/60 mb-1.5">発送・配送について</label>
                    <input value={form.shippingNote} onChange={e => setForm(f => ({ ...f, shippingNote: e.target.value }))} className="w-full border border-stone-200 rounded-xl px-4 py-2.5 text-sm font-sans outline-none focus:border-gold transition-colors" placeholder="例：ご注文確認後3〜5営業日以内に発送" />
                  </div>
                </div>
                <div className="bg-white rounded-2xl p-6 border border-stone-100">
                  <h3 className="font-sans text-sm font-medium text-charcoal mb-3">公開設定</h3>
                  <div className="flex gap-3">
                    {[{ v: "public", l: "公開", icon: "ri-earth-line" }, { v: "draft", l: "下書き", icon: "ri-draft-line" }].map((opt) => (
                      <button key={opt.v} onClick={() => setForm(f => ({ ...f, visibility: opt.v }))} className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-sans border transition-colors cursor-pointer whitespace-nowrap ${form.visibility === opt.v ? "border-deep-green bg-deep-green/5 text-deep-green" : "border-stone-200 text-charcoal/50 hover:border-stone-300"}`}>
                        <i className={opt.icon} />{opt.l}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mt-6">
              {step > 1 && (
                <button onClick={() => setStep(s => s - 1)} className="px-5 py-2.5 rounded-full border border-stone-200 text-charcoal/60 text-sm font-sans cursor-pointer hover:border-stone-300 transition-colors whitespace-nowrap">
                  ← 戻る
                </button>
              )}
              <div className={step === 1 ? "ml-auto" : ""}>
                <button onClick={handleComplete} className="bg-deep-green text-white px-8 py-2.5 rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap">
                  {step < 4 ? "次へ →" : "出品する →"}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
